import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheck, FaCrown, FaStar, FaInfinity } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';
import { config } from '../config';
import { Icon } from '../components/common/Icon';
import { PaymentModal } from '../components/subscription/PaymentModal';
import { UpdatePaymentModal } from '../components/subscription/UpdatePaymentModal';
import { CancellationModal } from '../components/subscription/CancellationModal';
import SignInModal from '../components/auth/SignInModal';
import { authService } from '../services/auth';
import { getCouponCookie, setCouponCookie, clearCouponCookie } from '../utils/cookieManager';

const PageContainer = styled.div`
  min-height: 100vh;
  
  padding: 20px 20px 40px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  padding: 20px 20px;
  border-radius: 12px;
  margin-bottom: 40px;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 30px;
  opacity: 0.9;
`;
const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 30px;
  color: white;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto 20px;
  line-height: 1.4;
  font-style: italic;
`;

const SubscriptionSummaryBox = styled.div`
  background: rgba(40, 40, 40, 0.95);
  border-radius: 16px;
  padding: 24px 32px;
  margin: 0 auto 32px;
  max-width: 800px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  color: white;
`;

const SubscriptionSummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SubscriptionSummaryTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  color: white;
`;

const SubscriptionStatusBadge = styled.div<{ $status: string }>`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.$status) {
      case 'active':
        return `
          background: linear-gradient(135deg, #28a745, #34ce57);
          color: white;
        `;
      case 'trialing':
        return `
          background: linear-gradient(135deg, #17a2b8, #20c997);
          color: white;
        `;
      case 'past_due':
        return `
          background: linear-gradient(135deg, #dc3545, #e74c3c);
          color: white;
          animation: pulse 2s infinite;
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
        `;
    }
  }}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const SubscriptionDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SubscriptionDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SubscriptionDetailLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const SubscriptionDetailValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
`;

const PastDueActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PastDueMessage = styled.div`
  flex: 1;
  color: #ffc107;
  font-size: 0.95rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 8px;
  }
`;

const UpdatePaymentButtonInSummary = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #dc3545, #e74c3c);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, #c82333, #dc2626);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TiersWrapper = styled.div`
  position: relative;
  margin-bottom: 60px;
  width: calc(100vw - 220px);
  left: calc(50% - 50vw + 110px);
  
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
    width: 100%;
    margin-left: 0;
    left: 0;
  }
`;

const TiersContainer = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 60px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: visible;
    padding: 20px;
  }
`;

const ScrollButton = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$direction === 'left' ? 'left: 20px' : 'right: 20px'};
  background: rgba(40, 40, 40, 0.95);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  opacity: 0;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  ${TiersWrapper}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background: rgba(102, 126, 234, 0.9);
    border-color: #667eea;
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TierCard = styled.div<{ $isPopular?: boolean; $isCurrentTier?: boolean; $isBestValue?: boolean }>`
  background: rgb(40, 40, 40);
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: ${props => props.$isCurrentTier ? '3px solid #28a745' : props.$isBestValue ? '3px solid #ffd700' : props.$isPopular ? '3px solid #2196f3' : '2px solid rgba(255, 255, 255, 0.1)'};
  width: 380px;
  min-width: 380px;
  max-width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1400px) {
    width: 340px;
    min-width: 340px;
    max-width: 340px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.$isCurrentTier ? '#28a745' : props.$isBestValue ? '#ffd700' : props.$isPopular ? '#2196f3' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #333;
  padding: 8px 24px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CurrentTierBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #28a745, #34ce57);
  color: white;
  padding: 8px 24px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
`;

const TierIcon = styled.div<{ $color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 1.5rem;
`;

const TierName = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 8px;
  color: white;
`;

const TierPrice = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const PriceAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
`;

const PricePeriod = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;


const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
`;

const CheckIcon = styled.div`
  color: #28a745;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectButton = styled.button<{ $variant: 'primary' | 'secondary' | 'current'; disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #5a67d8, #68637a);
            transform: translateY(-2px);
          }
        `;
      case 'secondary':
        return `
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.2);
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.2);
            border-color: #667eea;
          }
        `;
      case 'current':
        return `
          background: linear-gradient(135deg, #28a745, #34ce57);
          color: white;
          cursor: default;
          &:hover {
            transform: none;
          }
        `;
    }
  }}
`;

const FeaturesSection = styled.div`
  background: rgb(40, 40, 40);
  border-radius: 16px;
  padding: 48px 32px;
  margin-bottom: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FeaturesTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 40px;
  color: white;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 24px;
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 2rem;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: white;
`;

const FeatureDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

interface SubscriptionTier {
  id: string;
  tier_name: string;
  tier: number;
  description?: string;
  monthly: number;
  yearly: number;
  monthly_coins: number;
  monthly_listens: number;
  can_customize: boolean;
  can_create: boolean;
  private_assets: boolean;
  private_scripts: boolean;
  is_protected: boolean;
  can_download: boolean;
  exclusive_assets: boolean;
  custom_voices: number;
  is_enabled: boolean;
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
}

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const BillingOption = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: 2px solid ${props => props.$active ? '#667eea' : 'rgba(255, 255, 255, 0.2)'};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #667eea;
  }
`;

const BillingSavings = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #28a745, #34ce57);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  margin-left: 8px;
`;

// Coupon Section Styles
const CouponSection = styled.div`
  max-width: 600px;
  margin: 0 auto 32px;
  background: rgba(40, 40, 40, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
`;

const CouponInputContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CouponInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ApplyCouponButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RemoveCouponButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.5);
  background: transparent;
  color: #ef4444;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: #ef4444;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CouponMessage = styled.div<{ $isError?: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${props => props.$isError
    ? 'rgba(239, 68, 68, 0.1)'
    : 'rgba(40, 167, 69, 0.1)'};
  color: ${props => props.$isError ? '#ef4444' : '#28a745'};
  border: 1px solid ${props => props.$isError
    ? 'rgba(239, 68, 68, 0.3)'
    : 'rgba(40, 167, 69, 0.3)'};
`;

const CouponDiscountBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const TierDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 16px 0 24px;
  min-height: 80px;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const FirstMonthBadge = styled.div`
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: inline-block;
`;

const TwoMonthsFreeBadge = styled.div`
  background: linear-gradient(135deg, #28a745, #34ce57);
  color: white;
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: inline-block;
`;

const TrialText = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
`;

const CancelAnytime = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  
  svg {
    color: #28a745;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.5);
  color: #ef4444;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 12px;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: #ef4444;
  }
`;

const ResumeButton = styled.button`
  background: transparent;
  border: 1px solid rgba(40, 167, 69, 0.5);
  color: #28a745;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 12px;
  
  &:hover {
    background: rgba(40, 167, 69, 0.1);
    border-color: #28a745;
  }
`;

const UpdatePaymentButton = styled.button`
  background: transparent;
  border: 1px solid rgba(102, 126, 234, 0.5);
  color: #667eea;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 12px;
  
  &:hover {
    background: rgba(102, 126, 234, 0.1);
    border-color: #667eea;
  }
`;

const SubscriptionEndDate = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 8px;
  text-align: center;
`;

// Subscription Change Modal Components
const ChangeModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ChangeModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ChangeModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ChangeModalTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const ChangeModalCloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  margin-left: 16px;
  
  &:hover {
    color: white;
  }
`;

const ChangeModalSection = styled.div`
  margin-bottom: 24px;
`;

const ChangeModalSectionTitle = styled.h3`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const PlanComparisonCard = styled.div<{ $isNew?: boolean }>`
  background: ${props => props.$isNew ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$isNew ? 'rgba(40, 167, 69, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const PlanName = styled.div`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div`
  color: #4ade80;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PlanPeriod = styled.div`
  color: #999;
  font-size: 0.9rem;
`;

const ProrationDetails = styled.div`
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ProrationTitle = styled.div`
  color: #667eea;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProrationAmount = styled.div<{ $isCredit?: boolean }>`
  color: ${props => props.$isCredit ? '#28a745' : '#667eea'};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ProrationDescription = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ChangeModalActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const ChangeModalButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    &:hover {
      background: linear-gradient(135deg, #5a67d8, #68637a);
      transform: translateY(-2px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Disclaimer = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 20px;
  margin-bottom: 40px;
  font-style: italic;
`;

const Subscription: React.FC = () => {
  const { user } = useUser();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);
  const [isLoadingResume, setIsLoadingResume] = useState(false);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = useState(false);
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);
  const [changeModalData, setChangeModalData] = useState<{
    tier: SubscriptionTier;
    actionType: 'upgrade' | 'downgrade' | 'change';
    prorationAmount: number;
    showProration: boolean;
    creditInfo?: {
      hasExistingCredit: boolean;
      creditApplied: number;
      actualCharge: number;
    };
  } | null>(null);
  const [isProcessingChange, setIsProcessingChange] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    coupons: Array<{
      tier: number;
      billingPeriod: 'monthly' | 'yearly';
      discountPercent: number;
      duration: 'once' | 'repeating';
      durationMonths: number | null;
    }>;
  } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    fetchSubscriptionTiers();
    if (user) {
      fetchUserSubscription();
    }
  }, [user]);

  const fetchUserSubscription = async () => {
    try {
      const token = authService.getToken();
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/stripe/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserSubscription(data.subscription);

        // Set billing period based on current subscription
        if (data.subscription && data.subscription.billing_period) {
          setBillingPeriod(data.subscription.billing_period as 'monthly' | 'yearly');
        }
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    }
  };

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/subscription-tiers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Keep tiers in their natural order (by tier number)
        setTiers(data.sort((a: SubscriptionTier, b: SubscriptionTier) => a.tier - b.tier));
      }
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tierName: string) => {
    switch (tierName.toLowerCase()) {
      case 'seeker': return '🔍';
      case 'apprentice': return '📚';
      case 'journeyman': return '🌀';
      case 'master': return '🧘';
      default: return '✨';
    }
  };

  const getTierColor = (tierName: string) => {
    switch (tierName.toLowerCase()) {
      case 'seeker': return 'linear-gradient(135deg, #74b9ff, #0984e3)';
      case 'apprentice': return 'linear-gradient(135deg, #fd79a8, #e84393)';
      case 'journeyman': return 'linear-gradient(135deg, #fdcb6e, #e17055)';
      case 'master': return 'linear-gradient(135deg, #9b59b6, #8e44ad)';
      default: return 'linear-gradient(135deg, #667eea, #764ba2)';
    }
  };

  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `$${price}`;
  };

  const getButtonText = (tier: SubscriptionTier) => {
    // For free tier (monthly = 0), show button if not logged in OR if current tier
    if (Number(tier.monthly) === 0 && user && !isCurrentTier(tier.tier)) {
      return null; // Don't show button for logged in users who aren't on free tier
    }
    
    if (isCurrentTier(tier.tier)) return 'Current Plan';
    
    // If user has active subscription
    if (hasActiveSubscription()) {
      if (tier.tier < (user?.tier || 0)) {
        return 'Downgrade Your Journey';
      } else if (tier.tier > (user?.tier || 0)) {
        return 'Upgrade Your Journey';
      } else if (tier.tier === (user?.tier || 0)) {
        // Same tier, different billing period
        if (billingPeriod === 'yearly' && userSubscription?.billing_period === 'monthly') {
          return 'Switch to Yearly';
        } else if (billingPeriod === 'monthly' && userSubscription?.billing_period === 'yearly') {
          return 'Switch to Monthly';
        }
      }
    }
    
    // For users without subscription
    if (tier.monthly === 0) return 'Get Started';
    if (billingPeriod === 'yearly' && !hasActiveSubscription()) return 'Begin Your Free Trial';
    if (tier.tier_name === 'Master' || tier.tier_name === 'Journeyman') return 'Start Creating Today';
    return 'Begin Your Journey';
  };
  
  const hasActiveSubscription = () => {
    return user && user.subscription_status && user.tier > 1 &&
           (user.subscription_status === 'active' || user.subscription_status === 'trialing' || user.subscription_status === 'past_due');
  };
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    const cardWidth = window.innerWidth > 1400 ? 404 : 364; // Card width + gap
    const scrollAmount = cardWidth;
    const newScrollPosition = containerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
    containerRef.current.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
  };

  // Calculate discounted price based on applied coupon
  const getDiscountedPrice = (tier: SubscriptionTier): number | null => {
    if (!appliedCoupon || hasActiveSubscription()) return null;

    const basePrice = billingPeriod === 'monthly' ? Number(tier.monthly) : Number(tier.yearly);
    if (basePrice === 0) return null;

    // Find the coupon for this tier and billing period
    const couponForTier = appliedCoupon.coupons.find(
      c => c.tier === tier.tier && c.billingPeriod === billingPeriod
    );

    if (!couponForTier) return null;

    const discount = (basePrice * couponForTier.discountPercent) / 100;
    return basePrice - discount;
  };

  // Get coupon details for a specific tier
  const getCouponForTier = (tier: SubscriptionTier) => {
    if (!appliedCoupon) return null;
    return appliedCoupon.coupons.find(
      c => c.tier === tier.tier && c.billingPeriod === billingPeriod
    );
  };

  // Coupon handlers
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    setCouponError('');

    try {
      const response = await fetch(`${config.apiUrl}/coupons/validate-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: couponCode
        })
      });

      const data = await response.json();

      if (data.valid && data.coupons) {
        setAppliedCoupon({
          code: couponCode,
          coupons: data.coupons
        });
        setCouponCookie(couponCode);
        setCouponError('');
      } else {
        setCouponError(data.error || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('Failed to validate coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
    clearCouponCookie();
  };

  // Load coupons only for non-subscribed users
  useEffect(() => {
    // If user has active subscription, skip all coupon loading
    if (hasActiveSubscription()) {
      return;
    }

    // Now safe to load coupons - check URL param first (support both 'coupon' and 'code')
    const params = new URLSearchParams(window.location.search);
    const couponParam = params.get('coupon') || params.get('code');

    if (couponParam) {
      // URL parameter takes precedence
      const upperCode = couponParam.toUpperCase();
      setCouponCode(upperCode);
      setCouponCookie(upperCode);

      // Track click
      fetch(`${config.apiUrl}/coupons/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponParam })
      }).catch(err => console.error('Error tracking coupon click:', err));

      // Auto-validate and apply the coupon from URL
      fetch(`${config.apiUrl}/coupons/validate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: upperCode })
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid && data.coupons) {
            setAppliedCoupon({
              code: upperCode,
              coupons: data.coupons
            });
          }
        })
        .catch(err => console.error('Error validating URL coupon:', err));
      return; // Don't check cookie if URL param exists
    }

    // Check cookie next
    const savedCoupon = getCouponCookie();
    if (savedCoupon) {
      setCouponCode(savedCoupon);

      // Auto-validate saved coupon from cookie
      fetch(`${config.apiUrl}/coupons/validate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: savedCoupon })
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid && data.coupons) {
            setAppliedCoupon({
              code: savedCoupon,
              coupons: data.coupons
            });
          }
        })
        .catch(err => console.error('Error validating saved coupon:', err));
      return; // Don't check default if cookie exists
    }

    // Finally, check for default coupon (only if no URL param or cookie)
    const loadDefaultCoupon = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/coupons/default-code`);
        const data = await response.json();

        if (data.code) {
          // Validate the code across all tiers to get full coupon data
          const validateResponse = await fetch(`${config.apiUrl}/coupons/validate-all`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: data.code })
          });
          const validateData = await validateResponse.json();

          if (validateData.valid && validateData.coupons) {
            setCouponCode(data.code);
            setAppliedCoupon({
              code: data.code,
              coupons: validateData.coupons
            });
          }
        }
      } catch (error) {
        console.error('Error loading default coupon:', error);
      }
    };

    loadDefaultCoupon();
  }, [user]); // Only depends on user - subscription status is already in user object

  const handleSubscribe = async (tier: SubscriptionTier) => {
    if (isCurrentTier(tier.tier)) return;
    
    // Check if user is logged in
    if (!user) {
      setSignInModalOpen(true);
      return;
    }
    
    // Don't allow subscribing to free tier if not current
    if (Number(tier.monthly) === 0 && !isCurrentTier(tier.tier)) {
      return;
    }
    
    // Handle upgrade/downgrade for existing subscriptions
    if (hasActiveSubscription()) {
      await handleUpgradeDowngrade(tier);
    } else {
      // New subscription
      setSelectedTier(tier);
      setPaymentModalOpen(true);
    }
  };
  
  const handleUpgradeDowngrade = async (tier: SubscriptionTier) => {
    if (!userSubscription) return;
    
    setIsLoadingCancel(true);
    try {
      const token = authService.getToken();
      
      // Get the new price ID based on billing period
      const newPriceId = billingPeriod === 'yearly' 
        ? tier.stripe_price_id_yearly 
        : tier.stripe_price_id_monthly;

      if (!newPriceId) {
        console.error('Price ID not found for selected plan');
        setIsLoadingCancel(false);
        return;
      }

      // Preview the proration to show user what they'll be charged
      let prorationAmount = 0;
      let showProration = false;
      
      try {
        const previewResponse = await fetch(`${config.apiUrl}/stripe/subscription/preview-proration`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ newPriceId })
        });
        
        if (previewResponse.ok) {
          const previewData = await previewResponse.json();
          console.log('Proration preview response:', previewData);
          
          // Handle proration amount - negative values are credits
          prorationAmount = previewData.prorationAmount / 100; // Convert from cents
          showProration = true;
          
          if (prorationAmount < 0) {
            console.log(`Proration preview: CREDIT of $${Math.abs(prorationAmount).toFixed(2)}`);
          } else if (prorationAmount > 0) {
            console.log(`Proration preview: CHARGE of $${prorationAmount.toFixed(2)}`);
          } else {
            console.log('Proration preview: No charge or credit');
          }
          
          // Debug credit information
          if (previewData.creditInfo) {
            console.log('Credit info:', previewData.creditInfo);
          }
          
          // Debug raw invoice values
          if (previewData.rawInvoice) {
            console.log('Raw invoice values:', previewData.rawInvoice);
          }
          
          // Store credit info for modal display
          const creditInfo = previewData.creditInfo || null;
          
          // Show the elegant modal instead of window.confirm
          let actionType: 'upgrade' | 'downgrade' | 'change' = 'change';
          if (tier.tier > (user?.tier || 0)) {
            actionType = 'upgrade';
          } else if (tier.tier < (user?.tier || 0)) {
            actionType = 'downgrade';
          } else {
            // Same tier, different billing period
            actionType = 'change';
          }
          
          setChangeModalData({
            tier,
            actionType,
            prorationAmount,
            showProration,
            creditInfo
          });
          setChangeModalOpen(true);
          setIsLoadingCancel(false);
          return;
        } else {
          const errorData = await previewResponse.json();
          console.log('Proration preview failed with response:', errorData);
        }
      } catch (previewError) {
        console.log('Proration preview failed, will proceed without preview:', previewError);
        // Show modal without proration data
        let actionType: 'upgrade' | 'downgrade' | 'change' = 'change';
        if (tier.tier > (user?.tier || 0)) {
          actionType = 'upgrade';
        } else if (tier.tier < (user?.tier || 0)) {
          actionType = 'downgrade';
        } else {
          // Same tier, different billing period
          actionType = 'change';
        }
        
        setChangeModalData({
          tier,
          actionType,
          prorationAmount: 0,
          showProration: false
        });
        setChangeModalOpen(true);
      }
      
    } catch (error) {
      console.error('Error preparing subscription change:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoadingCancel(false);
    }
  };
  
  const confirmSubscriptionChange = async () => {
    if (!changeModalData || !userSubscription) return;
    
    setIsProcessingChange(true);
    try {
      const token = authService.getToken();
      
      // Get the new price ID based on billing period
      const newPriceId = billingPeriod === 'yearly' 
        ? changeModalData.tier.stripe_price_id_yearly 
        : changeModalData.tier.stripe_price_id_monthly;

      if (!newPriceId) {
        console.error('Price ID not found for selected plan');
        return;
      }

      // Proceed with the subscription change with billing cycle anchor and proration behavior
      const changeResponse = await fetch(`${config.apiUrl}/stripe/subscription/change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          newPriceId,
          prorationBehavior: 'create_prorations',
          billingCycleAnchor: 'now'
        })
      });
      
      if (changeResponse.ok) {
        const result = await changeResponse.json();
        console.log('Subscription updated successfully:', result);
        // Close modal and refresh data
        setChangeModalOpen(false);
        setChangeModalData(null);
        
        // Refresh all subscription data
        await Promise.all([
          fetchUserSubscription(),
          fetchSubscriptionTiers()
        ]);
        
        // Force a brief delay to ensure webhook has processed, then refresh page
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const error = await changeResponse.json();
        console.error('Failed to update subscription:', error);
        alert('Failed to update subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsProcessingChange(false);
    }
  };
  
  const handleCancelSubscription = async (reason: string, otherReason?: string, comments?: string) => {
    if (!userSubscription) return;
    
    setIsLoadingCancel(true);
    try {
      const token = authService.getToken();
      const response = await fetch(`${config.apiUrl}/stripe/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason,
          otherReason,
          comments
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Close the modal
        setCancellationModalOpen(false);
        // Refresh subscription data
        await fetchUserSubscription();
        
        // Brief delay then refresh page to show updated state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        console.error('Failed to cancel subscription');
        throw new Error('Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    } finally {
      setIsLoadingCancel(false);
    }
  };
  
  const handleResumeSubscription = async () => {
    if (!userSubscription) return;
    
    setIsLoadingResume(true);
    try {
      const token = authService.getToken();
      const response = await fetch(`${config.apiUrl}/stripe/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        // Refresh subscription data
        await fetchUserSubscription();
        
        // Brief delay then refresh page to show updated state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        console.error('Failed to resume subscription');
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
    } finally {
      setIsLoadingResume(false);
    }
  };

  const handlePaymentSuccess = async () => {
    // Refresh user data and subscription tiers
    await Promise.all([
      fetchUserSubscription(),
      fetchSubscriptionTiers()
    ]);
    console.log('Payment successful! Refreshing data...');
    
    // Force a brief delay to ensure webhook has processed, then refresh page
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleUpdatePaymentSuccess = async () => {
    // Close modal and show success message
    setUpdatePaymentModalOpen(false);
    console.log('Payment method updated successfully!');
    
    // Refresh page to show any updates
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const checkScrollButtons = () => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', checkScrollButtons);
    checkScrollButtons();
    
    // Initially scroll to show the 3 paid tiers, with Seeker mostly off-screen
    setTimeout(() => {
      if (window.innerWidth > 768) {
        const cardWidth = window.innerWidth > 1400 ? 380 : 340;
        const gap = 50;
        const scrollPosition = (cardWidth + gap) * 0.95; // Show only about 5% of first card
        container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }, 100);
    
    return () => container.removeEventListener('scroll', checkScrollButtons);
  }, [tiers]);

  const formatCoins = (coins: number) => {
    if (coins === 0) return 'None';
    if (coins >= 1000000) return `${(coins / 1000000).toFixed(1)}M`;
    if (coins >= 1000) return `${(coins / 1000).toFixed(0)}K`;
    return coins.toString();
  };
  
  const CoinImage = () => (
    <img 
      src="/images/YMcoin.png" 
      alt="coin" 
      style={{
        width: '16px', 
        height: '16px', 
        marginLeft: '0px',
        marginRight: '0px',
        verticalAlign: 'middle',
        display: 'inline-block'
      }} 
    />
  );


  const getTierFeatures = (tier: SubscriptionTier) => {
    const features = [];
    
    if (tier.monthly_listens === -1) {
      features.push('Unlimited meditation listens');
    } else {
      features.push(`${tier.monthly_listens} meditation listens per month`);
    }
    
    if (tier.monthly_coins > 0) {
      features.push(<>{formatCoins(tier.monthly_coins)}<CoinImage /> coins monthly</>);
    }
    
    if (tier.can_customize) features.push('Customize meditations');
    if (tier.can_create) features.push('Create your own meditations');
    if (tier.private_assets) features.push('Private audio assets');
    if (tier.private_scripts) features.push('Private meditation scripts');
    if (tier.can_download) features.push('Download meditations');
    if (tier.exclusive_assets) features.push('Exclusive premium audio assets');
    if (tier.custom_voices === -1) {
      features.push('Unlimited custom voices');
    } else if (tier.custom_voices > 0) {
      features.push(`${tier.custom_voices} custom voice${tier.custom_voices > 1 ? 's' : ''}`);
    }

    if (features.length === 0) {
      features.push('Basic meditation access');
    }
    
    return features;
  };

  const isCurrentTier = (tierLevel: number) => {
    // Only consider it current if both tier and billing period match
    if (!user || user.tier !== tierLevel) return false;
    
    // If user has an active subscription, also check billing period
    if (hasActiveSubscription() && userSubscription?.billing_period) {
      return userSubscription.billing_period === billingPeriod;
    }
    
    // For users without active subscription, just check tier
    return true;
  };

  const isPopularTier = (tierName: string) => {
    return tierName.toLowerCase() === 'journeyman';
  };

  const isBestValue = (tierName: string) => {
    return tierName.toLowerCase() === 'master';
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <HeaderSection>
            <Title>Loading...</Title>
          </HeaderSection>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <HeaderSection>
          <Title>
            "A journey of a thousand miles begins with a single step." - Lao Tzu
          </Title>


        {!user && (
        <Hero>
          <HeroTitle>Welcome to YouMeditation</HeroTitle>
          <HeroSubtitle>
            YouMeditation is a user driven community for creating and sharing meditations. You can create your own meditations, listen to one of the many meditations created by others, or customize an existing meditation to your liking.
            <br />
            <br />
            There are powerful AI tools to help you create or modify meditations, and easy to use audio editing tools, including brainwave entrainment sound generators, and a library of music and sounds to choose from.
            <br />
            <br />
            Subscribe below and create your perfect meditation today!
          </HeroSubtitle>
          
        </Hero>
      )}
                <Subtitle>
            Choose the plan that's right for you.
          </Subtitle>
        </HeaderSection>

        {hasActiveSubscription() && userSubscription && (
          <SubscriptionSummaryBox>
            <SubscriptionSummaryHeader>
              <SubscriptionSummaryTitle>Your Current Subscription</SubscriptionSummaryTitle>
              <SubscriptionStatusBadge $status={userSubscription.status}>
                {userSubscription.status === 'past_due' ? 'Payment Required' : userSubscription.status}
              </SubscriptionStatusBadge>
            </SubscriptionSummaryHeader>

            <SubscriptionDetails>
              <SubscriptionDetailItem>
                <SubscriptionDetailLabel>Plan</SubscriptionDetailLabel>
                <SubscriptionDetailValue>
                  {tiers.find(tier => tier.tier === user?.tier)?.tier_name || 'Unknown'} 
                  ({userSubscription.billing_period === 'yearly' ? 'Yearly' : 'Monthly'})
                </SubscriptionDetailValue>
              </SubscriptionDetailItem>

              <SubscriptionDetailItem>
                <SubscriptionDetailLabel>Next Billing Date</SubscriptionDetailLabel>
                <SubscriptionDetailValue>
                  {userSubscription.status === 'past_due' 
                    ? 'Past Due'
                    : userSubscription.cancel_at_period_end 
                      ? `Canceled - Access until ${formatDate(userSubscription.current_period_end)}`
                      : formatDate(userSubscription.current_period_end)}
                </SubscriptionDetailValue>
              </SubscriptionDetailItem>
            </SubscriptionDetails>

            {userSubscription.status === 'past_due' && (
              <PastDueActions>
                <PastDueMessage>
                  Your payment failed. Please update your payment method to continue enjoying your subscription.
                </PastDueMessage>
                <UpdatePaymentButtonInSummary onClick={() => setUpdatePaymentModalOpen(true)}>
                  Update Payment Method
                </UpdatePaymentButtonInSummary>
              </PastDueActions>
            )}

            {userSubscription.cancel_at_period_end && userSubscription.status !== 'past_due' && (
              <PastDueActions>
                <PastDueMessage>
                  Your subscription is set to cancel at the end of the current period. 
                </PastDueMessage>
                <UpdatePaymentButtonInSummary 
                  onClick={handleResumeSubscription}
                  style={{ 
                    background: 'linear-gradient(135deg, #28a745, #34ce57)',
                    color: 'white'
                  }}
                >
                  {isLoadingResume ? 'Processing...' : 'Resume Subscription'}
                </UpdatePaymentButtonInSummary>
              </PastDueActions>
            )}
          </SubscriptionSummaryBox>
        )}

        <BillingToggle>
          <BillingOption
            $active={billingPeriod === 'monthly'}
            onClick={() => setBillingPeriod('monthly')}
          >
            Monthly
          </BillingOption>
          <BillingOption
            $active={billingPeriod === 'yearly'}
            onClick={() => setBillingPeriod('yearly')}
          >
            Yearly
            <BillingSavings>Free Trial + 2 Months Free</BillingSavings>
          </BillingOption>
        </BillingToggle>

        {/* Coupon Section - Only show for non-subscribed users */}
        {!hasActiveSubscription() && (
          <CouponSection>
            <CouponInputContainer>
              <CouponInput
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                disabled={!!appliedCoupon}
              />
              {appliedCoupon ? (
                <RemoveCouponButton onClick={handleRemoveCoupon}>
                  Remove
                </RemoveCouponButton>
              ) : (
                <ApplyCouponButton
                  onClick={handleApplyCoupon}
                  disabled={validatingCoupon || !couponCode.trim()}
                >
                  {validatingCoupon ? 'Validating...' : 'Apply'}
                </ApplyCouponButton>
              )}
            </CouponInputContainer>

            {couponError && (
              <CouponMessage $isError={true}>
                {couponError}
              </CouponMessage>
            )}

            {appliedCoupon && !couponError && (
              <CouponMessage $isError={false}>
                ✓ Coupon "{appliedCoupon.code}" applied - discounts will show on eligible plans below
              </CouponMessage>
            )}
          </CouponSection>
        )}

        <TiersWrapper>
          <ScrollButton 
            $direction="left" 
            onClick={() => handleScroll('left')}
            disabled={!canScrollLeft}
          >
            ‹
          </ScrollButton>
          
          <TiersContainer ref={containerRef}>
            {tiers.map((tier) => (
            <TierCard 
              key={tier.id} 
              $isPopular={isPopularTier(tier.tier_name)}
              $isCurrentTier={isCurrentTier(tier.tier)}
              $isBestValue={isBestValue(tier.tier_name)}
            >
              {isCurrentTier(tier.tier) ? (
                <CurrentTierBadge>
                  Current Plan
                </CurrentTierBadge>
              ) : (
                <>
                  {isBestValue(tier.tier_name) && (
                    <PopularBadge>
                      <Icon icon={FaCrown} size={16} /> Best Value
                    </PopularBadge>
                  )}
                  
                  {isPopularTier(tier.tier_name) && !isBestValue(tier.tier_name) && (
                    <PopularBadge>
                      <Icon icon={FaStar} size={16} /> Most Popular
                    </PopularBadge>
                  )}
                </>
              )}

              <TierIcon $color={getTierColor(tier.tier_name)}>
                {getTierIcon(tier.tier_name)}
              </TierIcon>

              <TierName>{tier.tier_name}</TierName>

              {tier.description && (
                <TierDescription>{tier.description}</TierDescription>
              )}

              <TierPrice>
                {/* Show coupon discount badge */}
                {!hasActiveSubscription() && appliedCoupon && getDiscountedPrice(tier) !== null && (() => {
                  const couponForTier = getCouponForTier(tier);
                  return couponForTier && (
                    <CouponDiscountBadge>
                      {appliedCoupon.code}: {couponForTier.discountPercent}% off
                    </CouponDiscountBadge>
                  );
                })()}
                {billingPeriod === 'yearly' && Number(tier.yearly) > 0 && Number(tier.yearly) < Number(tier.monthly) * 12 && (
                  <TwoMonthsFreeBadge>2 Months Free*</TwoMonthsFreeBadge>
                )}
                {!hasActiveSubscription() && billingPeriod === 'yearly' && Number(tier.yearly) > 0 && (
                  <TrialText>Free 3-day trial, then</TrialText>
                )}
                <PriceAmount>
                  {(() => {
                    const discountedPrice = getDiscountedPrice(tier);
                    if (discountedPrice !== null) {
                      return formatPrice(discountedPrice);
                    }
                    return billingPeriod === 'monthly'
                      ? formatPrice(Number(tier.monthly))
                      : Number(tier.yearly) > 0 ? `$${tier.yearly}` : 'Free';
                  })()}
                </PriceAmount>
                <PricePeriod>
                  {Number(tier.monthly) > 0 || Number(tier.yearly) > 0 ?
                    (() => {
                      const couponForTier = getCouponForTier(tier);
                      const hasDiscount = appliedCoupon && getDiscountedPrice(tier) !== null && couponForTier;

                      if (billingPeriod === 'monthly') {
                        if (hasDiscount) {
                          return couponForTier.duration === 'once'
                            ? `first month, then $${tier.monthly}/month`
                            : `for ${couponForTier.durationMonths} months, then $${tier.monthly}/month`;
                        }
                        return '/month';
                      } else {
                        if (hasDiscount) {
                          return couponForTier.duration === 'once'
                            ? `first year, then $${tier.yearly}/year`
                            : `for ${couponForTier.durationMonths} months`;
                        }
                        return '/year';
                      }
                    })()
                    : ''
                  }
                </PricePeriod>
              </TierPrice>

              <FeaturesList>
                {getTierFeatures(tier).map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckIcon>
                      <Icon icon={FaCheck} size={16} />
                    </CheckIcon>
                    {feature}
                  </FeatureItem>
                ))}
              </FeaturesList>

              {getButtonText(tier) && (
                <SelectButton 
                  $variant={
                    isCurrentTier(tier.tier) ? 'current' : 
                    isBestValue(tier.tier_name) || isPopularTier(tier.tier_name) ? 'primary' : 'secondary'
                  }
                  onClick={() => handleSubscribe(tier)}
                  disabled={isLoadingCancel}
                >
                  {isLoadingCancel && hasActiveSubscription() && !isCurrentTier(tier.tier) ? 'Processing...' : getButtonText(tier)}
                </SelectButton>
              )}
              
              {isCurrentTier(tier.tier) && hasActiveSubscription() && (
                <>
                  {userSubscription?.cancel_at_period_end ? (
                    <>
                      <ResumeButton 
                        onClick={handleResumeSubscription}
                        disabled={isLoadingResume}
                      >
                        {isLoadingResume ? 'Processing...' : 'Resume Subscription'}
                      </ResumeButton>
                      <SubscriptionEndDate>
                        Subscription ends {formatDate(userSubscription?.current_period_end)}
                      </SubscriptionEndDate>
                    </>
                  ) : (
                    Number(tier.monthly) > 0 && (
                      <>
                        <UpdatePaymentButton 
                          onClick={() => setUpdatePaymentModalOpen(true)}
                        >
                          Update Payment Method
                        </UpdatePaymentButton>
                        <CancelButton 
                          onClick={() => setCancellationModalOpen(true)}
                          disabled={isLoadingCancel}
                        >
                          {isLoadingCancel ? 'Processing...' : 'Cancel Subscription'}
                        </CancelButton>
                      </>
                    )
                  )}
                </>
              )}
              
              {tier.tier_name === 'Seeker' ? (
                <CancelAnytime>
                  <Icon icon={FaCheck} size={14} />
                  Free forever
                </CancelAnytime>
              ) : (
                Number(tier.monthly) > 0 && !isCurrentTier(tier.tier) && (
                  <CancelAnytime>
                    <Icon icon={FaCheck} size={14} />
                    Cancel anytime
                  </CancelAnytime>
                )
              )}
            </TierCard>
            ))}
          </TiersContainer>
          
          <ScrollButton 
            $direction="right" 
            onClick={() => handleScroll('right')}
            disabled={!canScrollRight}
          >
            ›
          </ScrollButton>
        </TiersWrapper>
        
        {billingPeriod === 'yearly' && (
          <Disclaimer>
            *Two months free when paid yearly vs monthly.
          </Disclaimer>
        )}
        <HeaderSection>
        <Title>
          "Don’t count the days, make the days count." – Muhammad Ali
        </Title>
        </HeaderSection>
        <FeaturesSection>
          <FeaturesTitle>Why Choose YouMeditation?</FeaturesTitle>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <Icon icon={FaInfinity} size={32} />
              </FeatureIcon>
              <FeatureTitle>Unlimited Possibilities</FeatureTitle>
              <FeatureDescription>
                Create, customize, and explore an endless library of guided and unguided meditations 
                tailored to your personal journey.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <Icon icon={FaCrown} size={32} />
              </FeatureIcon>
              <FeatureTitle>Premium Content</FeatureTitle>
              <FeatureDescription>
                Access exclusive meditations, advanced features, and premium audio content 
                created by mindfulness experts.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <Icon icon={FaStar} size={32} />
              </FeatureIcon>
              <FeatureTitle>Personalized Experience</FeatureTitle>
              <FeatureDescription>
                AI-powered assistance in creating and personalizing the meditation that is perfect for you, so you can grow and explore the way you desire.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </FeaturesSection>
      </ContentContainer>
      
      {/* Payment Modal */}
      {selectedTier && (
        <PaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          tier={selectedTier}
          billingPeriod={billingPeriod}
          onSuccess={handlePaymentSuccess}
          couponCode={appliedCoupon?.code}
        />
      )}
      
      {/* Sign In Modal */}
      <SignInModal
        isOpen={signInModalOpen}
        onClose={() => setSignInModalOpen(false)}
        onSuccess={() => {
          setSignInModalOpen(false);
          // After sign in, retry the subscription action
          if (selectedTier) {
            handleSubscribe(selectedTier);
          }
        }}
      />
      
      {/* Update Payment Method Modal */}
      <UpdatePaymentModal
        isOpen={updatePaymentModalOpen}
        onClose={() => setUpdatePaymentModalOpen(false)}
        onSuccess={handleUpdatePaymentSuccess}
      />

      <CancellationModal
        isOpen={cancellationModalOpen}
        onClose={() => setCancellationModalOpen(false)}
        onConfirm={handleCancelSubscription}
      />
      
      {/* Subscription Change Modal */}
      {changeModalOpen && changeModalData && (
        <ChangeModalOverlay onClick={() => !isProcessingChange && setChangeModalOpen(false)}>
          <ChangeModalContent onClick={(e) => e.stopPropagation()}>
            <ChangeModalHeader>
              <ChangeModalTitle>
                {changeModalData.actionType === 'upgrade' ? 'Upgrade' : 
                 changeModalData.actionType === 'downgrade' ? 'Downgrade' : 
                 'Change'} Your Plan
              </ChangeModalTitle>
              <ChangeModalCloseButton 
                onClick={() => !isProcessingChange && setChangeModalOpen(false)}
                disabled={isProcessingChange}
              >
                ✕
              </ChangeModalCloseButton>
            </ChangeModalHeader>

            <ChangeModalSection>
              <ChangeModalSectionTitle>Plan Comparison</ChangeModalSectionTitle>
              
              <PlanComparisonCard>
                <PlanName>Current: {user?.tier && tiers.find(t => t.tier === user.tier)?.tier_name || 'Unknown'} {userSubscription?.billing_period === 'yearly' ? 'Yearly' : 'Monthly'}</PlanName>
                <PlanPrice>
                  ${userSubscription?.billing_period === 'yearly' 
                    ? tiers.find(t => t.tier === user?.tier)?.yearly || 0 
                    : tiers.find(t => t.tier === user?.tier)?.monthly || 0}
                </PlanPrice>
                <PlanPeriod>per {userSubscription?.billing_period === 'yearly' ? 'year' : 'month'}</PlanPeriod>
              </PlanComparisonCard>

              <PlanComparisonCard $isNew>
                <PlanName>New: {changeModalData.tier.tier_name}</PlanName>
                <PlanPrice>
                  ${billingPeriod === 'yearly' ? changeModalData.tier.yearly : changeModalData.tier.monthly}
                </PlanPrice>
                <PlanPeriod>per {billingPeriod === 'yearly' ? 'year' : 'month'}</PlanPeriod>
              </PlanComparisonCard>
            </ChangeModalSection>

            {changeModalData.showProration && (
              <ChangeModalSection>
                <ProrationDetails>
                  <ProrationTitle>
                    💰 Billing Details
                  </ProrationTitle>
                  
                  {changeModalData.creditInfo?.hasExistingCredit && changeModalData.creditInfo?.actualCharge === 0 ? (
                    // Case: Existing credits cover the entire charge
                    <>
                      <ProrationAmount $isCredit={true}>No Additional Charge</ProrationAmount>
                      <ProrationDescription>
                        Your existing credit of ${changeModalData.creditInfo.creditApplied.toFixed(2)} covers this change. No additional payment required.
                      </ProrationDescription>
                    </>
                  ) : changeModalData.creditInfo?.hasExistingCredit && changeModalData.creditInfo?.actualCharge > 0 ? (
                    // Case: Partial credit applied, still need to charge
                    <>
                      <ProrationAmount>Charge: ${changeModalData.creditInfo.actualCharge.toFixed(2)}</ProrationAmount>
                      <ProrationDescription>
                        After applying your ${changeModalData.creditInfo.creditApplied.toFixed(2)} credit, you'll be charged ${changeModalData.creditInfo.actualCharge.toFixed(2)} today.
                      </ProrationDescription>
                    </>
                  ) : changeModalData.prorationAmount !== 0 ? (
                    // Case: Standard proration without existing credits
                    <>
                      <ProrationAmount $isCredit={changeModalData.prorationAmount < 0}>
                        {changeModalData.prorationAmount > 0 
                          ? `Charge: $${changeModalData.prorationAmount.toFixed(2)}` 
                          : `Credit: $${Math.abs(changeModalData.prorationAmount).toFixed(2)}`}
                      </ProrationAmount>
                      <ProrationDescription>
                        {changeModalData.prorationAmount > 0 
                          ? `You'll be charged an additional $${changeModalData.prorationAmount.toFixed(2)} today.`
                          : `You'll receive a credit of $${Math.abs(changeModalData.prorationAmount).toFixed(2)} that will be applied to future invoices.`}
                      </ProrationDescription>
                    </>
                  ) : (
                    // Case: No charge or credit
                    <>
                      <ProrationAmount>$0.00</ProrationAmount>
                      <ProrationDescription>
                        No immediate charges. Any credits will be applied to future invoices.
                      </ProrationDescription>
                    </>
                  )}
                  
                  <ProrationDescription style={{ marginTop: '12px', fontSize: '0.8rem', opacity: 0.8 }}>
                    Your billing cycle will restart today, and you'll receive the full benefits and token allocation for this tier immediately.
                  </ProrationDescription>
                </ProrationDetails>
              </ChangeModalSection>
            )}

            <ChangeModalActions>
              <ChangeModalButton 
                $variant="secondary" 
                onClick={() => setChangeModalOpen(false)}
                disabled={isProcessingChange}
              >
                Cancel
              </ChangeModalButton>
              <ChangeModalButton 
                $variant="primary" 
                onClick={confirmSubscriptionChange}
                disabled={isProcessingChange}
              >
                {isProcessingChange && <LoadingSpinner />}
                {isProcessingChange 
                  ? 'Processing...' 
                  : `Confirm ${changeModalData.actionType === 'upgrade' ? 'Upgrade' : 
                                changeModalData.actionType === 'downgrade' ? 'Downgrade' : 
                                'Changes'}`}
              </ChangeModalButton>
            </ChangeModalActions>
          </ChangeModalContent>
        </ChangeModalOverlay>
      )}
    </PageContainer>
  );
};

export default Subscription;